import { useCallback, useState, useMemo, useEffect } from 'react';
import { Slider } from 'antd';
import { Button } from 'components/ui';
import { useUpdatePicturesMutation } from 'types/atlas-graphql';
import { FiltersProps } from './Filters';
import { LevelsButtonContainer, LevelsContainer } from './Controls.style';
import { usePermissions } from 'utils/usePermissions';
import { EDITOR } from 'utils/access-control/roles';
import { useTheme } from 'styled-components';

const toValue = ({ a = 1, b = 0 } = {}): [number, number] => {
  const min = -b / a;
  const max = (255 - b) / a;

  return [min, max];
};

export const Levels: React.FunctionComponent<
  FiltersProps & {
    menuOpen: boolean;
    onLevelsToggle: () => void;
    onLevelsChange?: (value: boolean) => void;
  }
> = ({
  levels: defaultValue,
  pictureId,
  pictureGlobalId,
  refetch,
  onChange,
  onLevelsToggle,
  onLevelsChange,
}) => {
  const theme = useTheme();
  const { hasRole } = usePermissions();
  const isEditor = hasRole(EDITOR);

  const { a, b } = defaultValue ?? { a: 1, b: 0 };
  const [value, setValue] = useState<[number, number]>(toValue(defaultValue));
  const [levels, setLevels] = useState<{ a?: number; b?: number }>({ a, b });
  const [saving, setSaving] = useState<boolean>(false);

  const hasDefaultLevels = useMemo(() => a !== 1 || b !== 0, [a, b]);
  const isEdited = useMemo(() => {
    const [defaultLevelsA, defaultLevelsB] = [defaultValue?.a ?? 1, defaultValue?.b ?? 0];
    const [currentLevelsA, currentLevelsB] = [levels?.a ?? 1, levels?.b ?? 0];
    return defaultLevelsA !== currentLevelsA || defaultLevelsB !== currentLevelsB;
  }, [defaultValue, levels]);

  const [updatePictures] = useUpdatePicturesMutation();

  useEffect(() => {
    onLevelsChange && onLevelsChange(isEdited);
  }, [onLevelsChange, isEdited]);

  const handleChange = useCallback(
    ([min, max]) => {
      const a = Number((255 / (max - min)).toFixed(4));
      const b = Math.round(-a * min);
      const levels = { a, b };
      setLevels(levels);
      setValue([min, max]);
      onChange({ levels });
    },
    [onChange]
  );

  const handleReset = useCallback(() => {
    hasDefaultLevels ? handleChange(toValue({ a, b })) : handleChange([0, 255]);
  }, [handleChange, hasDefaultLevels, a, b]);

  const handleSave = async () => {
    setSaving(true);
    await updatePictures({
      variables: {
        picturesToUpdate: [
          {
            globalId: pictureGlobalId,
            branchGlobalId: pictureId,
            viewSettings: {
              linear: [levels?.a ?? 1, levels?.b ?? 0],
            },
          },
        ],
      },
    });
    setSaving(false);

    refetch();
    onLevelsToggle();
  };

  return (
    <LevelsContainer onClick={e => e.stopPropagation()}>
      <span>Shadows</span>
      <Slider
        tooltipVisible={false}
        value={value[0]}
        id="image-levels"
        max={255}
        min={0}
        onChange={shadows => handleChange([shadows, value[1]])}
        step={1}
        trackStyle={{ background: theme.teal }}
        // @ts-ignore - this prop does in fact exist
        railStyle={{ background: theme.secondaryBackgroundColor }}
      />

      <span>Highlights</span>
      <Slider
        tooltipVisible={false}
        value={value[1]}
        id="image-levels"
        max={255}
        min={1}
        onChange={highlights => handleChange([value[0], highlights])}
        step={1}
        reverse
        trackStyle={{ background: theme.secondaryBackgroundColor }}
        // @ts-ignore - this prop does in fact exist
        railStyle={{ background: theme.teal }}
      />

      <LevelsButtonContainer>
        <Button size="small" onClick={handleReset} disabled={!isEdited}>
          Reset
        </Button>
        <div>
          {isEditor && (
            <Button
              size="small"
              type="primary"
              onClick={handleSave}
              loading={saving}
              disabled={!isEdited}
            >
              Save
            </Button>
          )}

          <Button size="small" onClick={onLevelsToggle}>
            Close
          </Button>
        </div>
      </LevelsButtonContainer>
    </LevelsContainer>
  );
};
